import { useState } from "react";
import type { JSX } from "react";
import { Link, Navigate } from "react-router";
import { ApiError } from "../api/errors";
import { ClockPage } from "../features/clock/ClockPage";
import { usePurchasePaths } from "../platform/PlatformContext";
import { hasAccessRight } from "../shared/auth/accessRights";
import { useI18n } from "../shared/i18n/I18nContext";
import { useMe } from "../shared/session/useMe";
import { useNavigationRoles } from "../shared/session/useNavigationRoles";
import { cx } from "../shared/ui/classNames";
import { PageHeader } from "../shared/ui/PageHeader";
import styles from "../shared/ui/ui.module.css";
import { requiredRights, routePaths, visibleNavigation } from "./navigation";
import { roleOverviewFor } from "./roleOverview";

/** The first screen after sign-in. */
export type StartScreen =
  | { readonly kind: "clock" }
  | { readonly kind: "redirect"; readonly path: string }
  | { readonly kind: "noAppFunctions" }
  | { readonly kind: "roleOverview" }
  | { readonly kind: "notRegistered" };

/** Code of `GET /me` for a signed-in account that no employee record of the tenant names yet. */
const notRegisteredCode = "TT-3001";

/**
 * Returns where a person starts. Everybody who records time, and everybody whose roles are not
 * known yet, starts on the clock. People without own records open their first navigation entry
 * other than the clock. Where that entry would be billing and the platform offers no way to buy
 * (the native apps, ADR-0087), a neutral page says that the app has no functions for them. Where
 * the roles have no screen of their own, only entries for everybody („Datenschutz“), as for
 * auditors and works councils, an overview names their functions instead (UX-026). An account the
 * server answered with TT-3001 belongs to no person yet (for example after the first sign-in through
 * the employer's single sign-on, before HR or SCIM links it): it cannot clock, so it gets a page that
 * says so instead of the clock (F-82).
 *
 * @param roles - Roles from `GET /me` or the last known roles (F-05), or `undefined` if unknown.
 * @param purchasePaths - Whether ways to buy may be shown on this platform.
 * @param notRegistered - Whether `GET /me` answered that the account belongs to no person (TT-3001).
 * @returns The start screen.
 */
export function startScreenFor(
  roles: readonly string[] | undefined,
  purchasePaths: boolean,
  notRegistered = false,
): StartScreen {
  let screen: StartScreen = { kind: "clock" };

  if (notRegistered) {
    screen = { kind: "notRegistered" };
  } else if (roles !== undefined && !hasAccessRight(roles, "AccessOwnRecords")) {
    const target = visibleNavigation(roles).find((item) => item.path !== routePaths.clock);

    if (target?.path === routePaths.billing && !purchasePaths) {
      screen = { kind: "noAppFunctions" };
    } else if (target !== undefined && requiredRights(target).length > 0) {
      screen = { kind: "redirect", path: target.path };
    } else {
      screen = { kind: "roleOverview" };
    }
  }

  return screen;
}

/**
 * Neutral page of the native apps for people whose roles have no function there, such as billing
 * administrators: no billing function and no hint where to pay (ADR-0087 decision 1). Preferences
 * and the sign-out stay on "Mehr".
 *
 * @returns The page element.
 */
function NoAppFunctionsPage(): JSX.Element {
  const { t } = useI18n();
  const page = (
    <div className={styles["page"]}>
      <PageHeader title={t("start.noAppFunctions.title")} />
      <p>{t("start.noAppFunctions.text")}</p>
    </div>
  );

  return page;
}

/** Properties of {@link NotRegisteredPage}. */
interface NotRegisteredPageProps {
  /** Asks the server again whether the account belongs to a person now. */
  readonly onRetry: () => void;
  /** Whether that question is running. */
  readonly retrying: boolean;
}

/**
 * Start page of an account that belongs to no person of the tenant yet (TT-3001, F-82): what that
 * means and who helps, the error code for support, and a button to ask again once HR has linked
 * the account. No clock, because nothing recorded here could be stored.
 *
 * @param props - Component properties.
 * @returns The page element.
 */
function NotRegisteredPage(props: NotRegisteredPageProps): JSX.Element {
  const { t } = useI18n();
  const page = (
    <div className={styles["page"]}>
      <PageHeader title={t("start.notRegistered.title")} />
      <section className={cx(styles["card"], styles["stack"])}>
        <p>{t("start.notRegistered.text")}</p>
        <p className={cx(styles["muted"], styles["errorCode"])}>
          {t("error.code", { code: notRegisteredCode })}
        </p>
        <div className={styles["row"]}>
          <button
            type="button"
            className={styles["button"]}
            disabled={props.retrying}
            onClick={props.onRetry}
          >
            {props.retrying ? t("error.retrying") : t("start.notRegistered.retry")}
          </button>
        </div>
      </section>
      <p className={styles["muted"]}>{t("start.notRegistered.account")}</p>
    </div>
  );

  return page;
}

/** Properties of {@link RoleOverviewPage}. */
interface RoleOverviewPageProps {
  /** Roles of the person. */
  readonly roles: readonly string[];
}

/**
 * Start page of roles without a screen of their own, such as auditors and works councils (UX-026):
 * their functions in the app with a link to each, and what the app does not offer them.
 *
 * @param props - Component properties.
 * @returns The page element.
 */
function RoleOverviewPage(props: RoleOverviewPageProps): JSX.Element {
  const { t } = useI18n();
  const overview = roleOverviewFor(props.roles);
  const page = (
    <div className={styles["page"]}>
      <PageHeader title={t("start.overview.title")} />
      <section aria-labelledby="start-functions" className={cx(styles["card"], styles["stack"])}>
        <h2 id="start-functions">{t("start.overview.functions")}</h2>
        <ul className={styles["stack"]}>
          {overview.functions.map((item) => (
            <li key={item.path}>
              <Link className={styles["linkButton"]} to={item.path}>
                {t(item.labelKey)}
              </Link>
            </li>
          ))}
        </ul>
      </section>
      {overview.notInApp.length === 0 ? null : (
        <section aria-labelledby="start-not-in-app" className={cx(styles["card"], styles["stack"])}>
          <h2 id="start-not-in-app">{t("start.overview.notInApp")}</h2>
          {overview.notInApp.map((key) => (
            <p key={key}>{t(key)}</p>
          ))}
        </section>
      )}
      <p className={styles["muted"]}>{t("start.overview.more")}</p>
    </div>
  );

  return page;
}

/** Whether the account belongs to no person, with the way to ask the server again. */
interface Registration {
  /** Whether the last settled answer of `GET /me` was TT-3001. */
  readonly notRegistered: boolean;
  /** Asks the server again. */
  readonly retry: () => void;
  /** Whether a question is running. */
  readonly retrying: boolean;
}

/**
 * Follows whether `GET /me` answered TT-3001. The value changes only with a settled answer: while
 * a request runs, TanStack Query reports a failed query as pending to every newly mounted observer
 * and asks again. If the start page then showed the clock, its hooks would mount new observers and
 * ask again on every answer, in a loop; so the page keeps what the last answer said.
 *
 * @returns The registration state.
 */
function useRegistration(): Registration {
  const me = useMe();
  const [notRegistered, setNotRegistered] = useState(false);
  const answered = me.isSuccess
    ? false
    : me.isError && !me.isFetching
      ? me.error instanceof ApiError && me.error.code === notRegisteredCode
      : null;

  // Adjusting state to a new answer during rendering, as React recommends instead of an effect.
  if (answered !== null && answered !== notRegistered) {
    setNotRegistered(answered);
  }

  const registration: Registration = {
    notRegistered,
    retry: () => {
      void me.refetch();
    },
    retrying: me.isFetching,
  };

  return registration;
}

/**
 * The start page: the clock, which works offline from local data. People without own records,
 * such as billing administrators (T-134), have nothing to record and open their first screen, or
 * a neutral page in the native apps if that screen would be billing. Roles without a screen of
 * their own get an overview of their functions (UX-026); an account of no person a page that says
 * so (F-82).
 *
 * @returns The clock, a redirect, the neutral page, the overview or the page of an unlinked account.
 */
export function StartPage(): JSX.Element {
  const roles = useNavigationRoles();
  const purchasePaths = usePurchasePaths();
  const registration = useRegistration();
  const screen = startScreenFor(roles, purchasePaths, registration.notRegistered);

  const page =
    screen.kind === "clock" ? (
      <ClockPage />
    ) : screen.kind === "redirect" ? (
      <Navigate to={screen.path} replace />
    ) : screen.kind === "roleOverview" ? (
      <RoleOverviewPage roles={roles ?? []} />
    ) : screen.kind === "notRegistered" ? (
      <NotRegisteredPage onRetry={registration.retry} retrying={registration.retrying} />
    ) : (
      <NoAppFunctionsPage />
    );
  return page;
}
